import { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { move } from "@dnd-kit/helpers";
import { useMutation } from "@tanstack/react-query";

import { asMutation } from "simplydo/core/query";
import { styled } from "simplydo/react/styled";
import {
  Button,
  Divider,
  Form,
  Icon,
  Message,
  Modal,
  Segment,
  type SemanticICONS,
} from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import constants from "utils/constants";
import util from "utils/utils";

import { DndContext } from "components/lib/dnd/DndContext";
import { Sortable } from "components/lib/dnd/Sortable";
import { makeResult } from "components/lib/dnd/dndHelpers";

import type { AdminSidebarItem, OrgNavLink } from "../../types";
import { useAdminSidebar } from "../../useAdminSidebar";

// Specificity is needed to survive the flex layout SegmentContainer applies to
// every nested segment
const EntrySegment = styled(Segment)`
  && {
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 0 0 auto;
    min-height: 45px;
    padding: 0.6em 1em;
    margin: 0;
  }
`;

const VISIBLE_GROUP = "enabled-admin-sidebar";
const HIDDEN_GROUP = "disabled-admin-sidebar";

const flatOrgPermissions = constants.permissions.org.flatMap(
  (category) => category.permissions,
);

const emptyLink = (): OrgNavLink => ({
  id: "",
  title: "",
  location: "",
  type: "link",
});

const toDatabase = (entries: AdminSidebarItem[]) =>
  entries.map((item) =>
    item.type ? { id: item.id, type: item.type } : { id: item.id },
  );

const AdminSidebarEntry = ({
  item,
  onEditLink,
  onDeleteLink,
}: {
  item: AdminSidebarItem;
  onEditLink: (link: OrgNavLink) => void;
  onDeleteLink: (linkId: string) => void;
}) => {
  const { t } = useTranslation();
  const permissions = (item.permissions ?? [])
    .map((permission) =>
      t(
        flatOrgPermissions.find((p) => p.key === permission)?.title ??
          permission,
      ),
    )
    .join(", ");

  return (
    <EntrySegment>
      <Icon name={item.icon as SemanticICONS} style={{ height: "1lh" }} />
      <div style={{ flex: 1 }}>
        {item.translate
          ? t("common:capitalise", { key: item.translate })
          : (item.title ??
            t("admin.settings.general.adminSidebar.inaccessibleEntry"))}
        <p style={{ fontSize: "0.7em" }}>
          {t(
            `admin.settings.general.adminSidebar.entryTypes.${item.type ?? "default"}`,
          )}
          {item.type === "link" ? `: ${item.location}` : null}
          {permissions
            ? `. ${t("admin.settings.general.adminSidebar.restricted", { permissions })}`
            : null}
          {item.canView === false
            ? `. ${t("admin.settings.general.adminSidebar.inaccessibleToYou")}`
            : null}
        </p>
      </div>
      {item.type === "link" ? (
        <>
          <Button
            icon="trash"
            size="mini"
            onClick={() => onDeleteLink(item.id)}
          />
          <Button
            size="mini"
            icon="edit"
            content={t("generic.edit")}
            onClick={() =>
              onEditLink({
                id: item.id,
                title: item.title ?? "",
                location: item.location ?? "",
                external: item.external,
                type: "link",
              })
            }
          />
        </>
      ) : null}
      <Icon name="bars" />
    </EntrySegment>
  );
};

const AdminSidebar = () => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const orgId = user?.ownerOrganisation?._id;
  const orgLinks = user?.ownerOrganisation?.links;
  const { configuredEntries, allItems } = useAdminSidebar();

  const [visibleEntries, setVisibleEntries] = useState(configuredEntries);
  const [hiddenEntries, setHiddenEntries] = useState<AdminSidebarItem[]>([]);
  const [unsaved, setUnsaved] = useState(false);
  const [editingLink, setEditingLink] = useState<OrgNavLink | null>(null);

  useEffect(() => {
    setVisibleEntries(configuredEntries);
  }, [configuredEntries]);

  useEffect(() => {
    setHiddenEntries(
      allItems.filter(
        (item) => !visibleEntries.find((entry) => entry.id === item.id),
      ),
    );
  }, [allItems, visibleEntries]);

  const updateOrgMutation = useMutation({
    mutationFn: asMutation(api.organisations.updateSettings)!,
    onSuccess: ({ adminSidebar, links }) => {
      if (!orgId) return;
      api.setQueryData(api.organisations.get, [orgId], (prev) => ({
        ...prev,
        adminSidebar,
        links,
      }));
      dispatch(actions.user.updateOrganisation("adminSidebar", adminSidebar));
      dispatch(actions.user.updateOrganisation("links", links));
      toast.success(t("generic.typeUpdated", { type: "Settings" }));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const saveSidebar = useCallback(
    (entries: AdminSidebarItem[]) => {
      if (!orgId) return;
      updateOrgMutation.mutate([orgId, { adminSidebar: toDatabase(entries) }]);
      setUnsaved(false);
    },
    [orgId, updateOrgMutation],
  );

  const saveLink = useCallback(() => {
    if (!orgId || !editingLink) return;
    const links = orgLinks ?? [];
    const existingIndex = links.findIndex((link) => link.id === editingLink.id);
    const updatedLinks = [...links];
    const updatedSidebar = [...visibleEntries];

    if (existingIndex !== -1) {
      updatedLinks[existingIndex] = { ...editingLink };
      const sidebarIndex = updatedSidebar.findIndex(
        (entry) => entry.id === editingLink.id,
      );
      if (sidebarIndex !== -1) {
        updatedSidebar[sidebarIndex] = {
          ...updatedSidebar[sidebarIndex]!,
          title: editingLink.title,
          location: editingLink.location,
          external: editingLink.external,
        };
        setVisibleEntries(updatedSidebar);
      }
    } else {
      updatedLinks.push({ ...editingLink });
    }

    updateOrgMutation.mutate([
      orgId,
      { links: updatedLinks, adminSidebar: toDatabase(updatedSidebar) },
    ]);
    setEditingLink(null);
  }, [editingLink, orgId, orgLinks, updateOrgMutation, visibleEntries]);

  const deleteLink = useCallback(
    (linkId: string) => {
      if (!orgId) return;
      util
        .confirm(
          t("generic.delete"),
          t("admin.settings.general.adminSidebar.deleteLinkConfirm"),
        )
        .then(() => {
          const updatedSidebar = visibleEntries.filter(
            (entry) => entry.id !== linkId,
          );
          setVisibleEntries(updatedSidebar);
          updateOrgMutation.mutate([
            orgId,
            {
              links: (orgLinks ?? []).filter((link) => link.id !== linkId),
              adminSidebar: toDatabase(updatedSidebar),
            },
          ]);
        })
        .catch(() => {});
    },
    [orgId, orgLinks, updateOrgMutation, visibleEntries, t],
  );

  if (!orgId) {
    return null;
  }

  return (
    <Segment>
      <h3>{t("admin.settings.general.adminSidebar.title")}</h3>
      <p>{t("admin.settings.general.adminSidebar.info")}</p>
      <p>
        {t("admin.settings.general.adminSidebar.pagesInfo")}{" "}
        <Link to="/admin/communication/pages">
          {t("admin.settings.general.adminSidebar.managePages")}
        </Link>
      </p>
      <Button
        primary
        size="tiny"
        disabled={!unsaved}
        content={t("generic.save")}
        onClick={() => saveSidebar(visibleEntries)}
      />
      <Button
        size="tiny"
        onClick={() => {
          util
            .confirm(
              t("admin.settings.general.adminSidebar.reset"),
              t("admin.settings.general.adminSidebar.resetConfirm"),
            )
            .then(() => {
              setUnsaved(false);
              updateOrgMutation.mutate([orgId, { adminSidebar: null }]);
            })
            .catch(() => {});
        }}
      >
        <Icon name="redo" />
        {t("admin.settings.general.adminSidebar.reset")}
      </Button>
      <Divider />
      <DndContext
        onDragOver={(event, _manager, setMoveResult) => {
          const { source, target } = event.operation;
          if (!source || !target) return;

          const targetGroup =
            "group" in target
              ? (target as { group?: string }).group
              : undefined;

          if (target.id === HIDDEN_GROUP || targetGroup === HIDDEN_GROUP) {
            const item = visibleEntries.find((entry) => entry.id === source.id);
            if (item?.required) {
              event.preventDefault();
              setMoveResult(makeResult(event, false));
              return;
            }
          }
          const result = move(
            { [VISIBLE_GROUP]: visibleEntries, [HIDDEN_GROUP]: hiddenEntries },
            event,
          );
          setVisibleEntries(result[VISIBLE_GROUP] ?? []);
          setHiddenEntries(result[HIDDEN_GROUP] ?? []);
          setUnsaved(true);
        }}
        onDidMove={(moveResult) => {
          if (
            !moveResult.didMove &&
            (moveResult.targetId === HIDDEN_GROUP ||
              moveResult.targetGroup === HIDDEN_GROUP)
          ) {
            toast.error(t("admin.settings.general.adminSidebar.cannotHide"));
          }
        }}
      >
        <h4>{t("admin.settings.general.adminSidebar.visibleEntries")}</h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {visibleEntries.map((item, index) => (
            <Sortable
              group={VISIBLE_GROUP}
              index={index}
              key={item.id}
              id={item.id}
            >
              <AdminSidebarEntry
                item={item}
                onEditLink={setEditingLink}
                onDeleteLink={deleteLink}
              />
            </Sortable>
          ))}
        </div>
        <h4>{t("admin.settings.general.adminSidebar.hiddenEntries")}</h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {!hiddenEntries.length ? (
            <Sortable
              group={HIDDEN_GROUP}
              index={0}
              disabled
              key={HIDDEN_GROUP}
              id={HIDDEN_GROUP}
            >
              <Message
                info
                content={t(
                  "admin.settings.general.adminSidebar.noHiddenEntries",
                )}
              />
            </Sortable>
          ) : null}
          {hiddenEntries.map((item, index) => (
            <Sortable
              group={HIDDEN_GROUP}
              index={index}
              key={item.id}
              id={item.id}
            >
              <AdminSidebarEntry
                item={item}
                onEditLink={setEditingLink}
                onDeleteLink={deleteLink}
              />
            </Sortable>
          ))}
        </div>
      </DndContext>
      <Divider hidden />
      <Button size="small" onClick={() => setEditingLink(emptyLink())}>
        <Icon name="plus" />
        {t("admin.settings.general.adminSidebar.addLink")}
      </Button>
      <Modal
        open={!!editingLink}
        onClose={() => setEditingLink(null)}
        mountNode={document.getElementById("semantic-modal-mount-node")}
      >
        <Modal.Header>
          {t("admin.settings.general.adminSidebar.linkTitle")}
        </Modal.Header>
        <Modal.Content>
          <Modal.Description>
            <p>{t("admin.settings.general.adminSidebar.linkInfo")}</p>
            <p>{t("admin.settings.general.adminSidebar.linkNote")}</p>
            <Form>
              <Form.Field>
                <label htmlFor="adminSidebarLinkTitle">
                  {t("admin.settings.general.adminSidebar.linkTitleLabel")}
                </label>
                <Input
                  id="adminSidebarLinkTitle"
                  type="text"
                  value={editingLink?.title ?? ""}
                  onChange={(e) =>
                    setEditingLink({
                      ...(editingLink ?? emptyLink()),
                      title: e.target.value,
                    })
                  }
                />
              </Form.Field>
              <Form.Field>
                <label htmlFor="adminSidebarLinkLocation">
                  {t("admin.settings.general.adminSidebar.linkLocationLabel")}
                </label>
                <Input
                  id="adminSidebarLinkLocation"
                  type="text"
                  placeholder="https://example.com"
                  value={editingLink?.location ?? ""}
                  onChange={(e) =>
                    setEditingLink({
                      ...(editingLink ?? emptyLink()),
                      location: e.target.value,
                    })
                  }
                />
              </Form.Field>
              <Form.Checkbox
                style={{ marginTop: 10 }}
                label={t("admin.settings.general.adminSidebar.linkNewTab")}
                checked={!!editingLink?.external}
                onChange={(_e, { checked }) =>
                  setEditingLink({
                    ...(editingLink ?? emptyLink()),
                    external: checked,
                  })
                }
              />
            </Form>
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions>
          <Button
            content={t("generic.cancel")}
            onClick={() => setEditingLink(null)}
          />
          <Button
            primary
            icon="checkmark"
            content={t("generic.save")}
            disabled={!editingLink?.title || !editingLink?.location}
            onClick={saveLink}
          />
        </Modal.Actions>
      </Modal>
    </Segment>
  );
};

export default AdminSidebar;
